import React, { useState, useEffect, useMemo, useRef } from "react";

import {
  HotelEntryTypeEnum,
  IIdLodgings,
  Lodging,
  ProductFeedTileTappedProperties,
  ProductFeedViewedProperties,
  PRODUCT_FEED_TILE_TAPPED,
  PRODUCT_FEED_RENDERED_EVENT,
  PRODUCT_FEED_VIEWED_EVENT,
  PRODUCT_FEED_NOT_RENDERED_EVENT,
  ProductFeedNotRenderedProperties,
  LodgingCollectionEnum,
} from "redmond";
import { RouteComponentProps } from "react-router";
import { HotelCrossSell } from "halifax";
import { InView } from "react-intersection-observer";
import { localCache, useIsTravelApp } from "@capone/common";
import { v4 as uuidv4 } from "uuid";
import { FlightToHotelCrossSellConnectorProps } from "./container";
import {
  useExperiments,
  getExperimentVariantCustomVariants,
  AVAILABLE,
  getExperimentVariant,
  PREMIER_COLLECTION_EXPERIMENT,
  LODGING_PROMOTIONS,
  LODGING_PROMOTIONS_AVAILABLE,
  LODGING_PROMOTIONS_VARIANTS,
  CACHE_HOTEL_TOKEN,
} from "../../context/experiments";
import { HOTEL_PATH_AVAILABILITY, HOTEL_PATH_SHOP } from "../../utils/urlPaths";
import {
  transformToStringifiedAvailabilityQuery,
  transformToStringifiedQuery,
} from "./utils/queryStringHelpers";

import { trackEvent } from "../../api/v0/analytics/trackEvent";

const CROSS_SELL_NOT_RENDERED_STATIC_PROPERTIES: Omit<
  ProductFeedNotRenderedProperties,
  "reason" | "channel"
> = {
  feed_type: "cross_sell",
  feed_placement: "booking_confirmation",
  feed_sub_placement: "below_fold",
  tile_type: "cross_sell_hotel",
  origin_product_type: "air",
};

export interface IFlightToHotelCrossSellProps
  extends FlightToHotelCrossSellConnectorProps,
  RouteComponentProps {
  isMobile?: boolean;
  className?: string;
  offerBannerText?: string;
}
export const FlightToHotelCrossSell = (props: IFlightToHotelCrossSellProps) => {
  const {
    lodgings,
    searchLocationResult,
    fromDate,
    untilDate,
    largestValueAccount,
    isMobile,
    hotelShopParams,
    history,
    setSelectedLodgingIndex,
    adultsCount,
    children,
    viewedHotelListProperties,
    className,
    latency,
    offerBannerText,
  } = props;

  const [lodgingsToDisplay, setLodgingsToDisplay] = useState<Lodging[]>([]);

  const { isTravelApp } = useIsTravelApp();
  const channel = isTravelApp ? "App" : isMobile ? "Mobile" : "Web";

  // Fire-once guards so a transient non-render state doesn't emit repeatedly.
  const noRewardsAccountNotRenderedFired = useRef(false);
  const allPremierFilteredNotRenderedFired = useRef(false);

  // Availability + dates arrived but there's no rewards account to price earn,
  // so the carousel can't render. Guarded above the early return below so it
  // still fires when `largestValueAccount` is the missing piece.
  useEffect(() => {
    if (
      !noRewardsAccountNotRenderedFired.current &&
      !!lodgings?.length &&
      !!fromDate &&
      !!untilDate &&
      !largestValueAccount
    ) {
      noRewardsAccountNotRenderedFired.current = true;
      trackEvent({
        eventName: PRODUCT_FEED_NOT_RENDERED_EVENT,
        properties: {
          ...CROSS_SELL_NOT_RENDERED_STATIC_PROPERTIES,
          reason: "no_rewards_account",
          channel,
        },
      });
    }
  }, [lodgings, fromDate, untilDate, largestValueAccount, channel]);

  if (!lodgings?.length || !fromDate || !untilDate || !largestValueAccount)
    return null;

  const expState = useExperiments();

  const lodgingPromotions = getExperimentVariantCustomVariants(
    expState.experiments,
    LODGING_PROMOTIONS,
    LODGING_PROMOTIONS_VARIANTS,
  );
  const isLodgingPromotionsExperiment = useMemo(
    () => lodgingPromotions === LODGING_PROMOTIONS_AVAILABLE,
    [lodgingPromotions],
  );

  const showEarnEnhancement =
    !!largestValueAccount && !!largestValueAccount.earn.hotelsMultiplier;

  const premierCollectionExperiment = getExperimentVariant(
    expState.experiments,
    PREMIER_COLLECTION_EXPERIMENT,
  );
  const isPremierCollectionEnabled = useMemo(
    () => premierCollectionExperiment === AVAILABLE,
    [premierCollectionExperiment],
  );

  const cacheHotelTokenExperiment = getExperimentVariant(
    expState.experiments,
    CACHE_HOTEL_TOKEN,
  );
  const isCacheHotelTokenEnabled = useMemo(
    () => cacheHotelTokenExperiment === AVAILABLE,
    [cacheHotelTokenExperiment],
  );

  useEffect(() => {
    const filteredLodgings = lodgings.filter(
      (l) => l.lodgingCollection !== LodgingCollectionEnum.Premier,
    );
    if (
      !allPremierFilteredNotRenderedFired.current &&
      lodgings.length > 0 &&
      filteredLodgings.length === 0
    ) {
      allPremierFilteredNotRenderedFired.current = true;
      trackEvent({
        eventName: PRODUCT_FEED_NOT_RENDERED_EVENT,
        properties: {
          ...CROSS_SELL_NOT_RENDERED_STATIC_PROPERTIES,
          reason: "all_premier_filtered",
          channel,
        },
      });
    }
    if (isMobile && filteredLodgings.length > 5) {
      setLodgingsToDisplay(filteredLodgings.slice(0, 5));
    } else if (!isMobile && filteredLodgings.length > 8) {
      setLodgingsToDisplay(filteredLodgings.slice(0, 8));
    } else {
      setLodgingsToDisplay(filteredLodgings);
    }
  }, [lodgings, isMobile, channel]);

  useEffect(() => {
    if (lodgingsToDisplay.length) {
      const xSellProps: ProductFeedViewedProperties = {
        feed_type: "cross_sell",
        feed_placement: "booking_confirmation",
        feed_sub_placement: "below_fold",
        tile_type: "cross_sell_hotel",
        origin_product_type: "air",
        channel: isTravelApp ? "App" : isMobile ? "Mobile" : "Web",
        latency: latency!,
      };
      trackEvent({
        eventName: PRODUCT_FEED_RENDERED_EVENT,
        properties: {
          ...xSellProps,
        },
      });
    }
  }, [lodgingsToDisplay]);

  const onViewAllHotelsCTAClick = () => {
    const xSellProps: ProductFeedTileTappedProperties = {
      feed_type: "cross_sell",
      feed_placement: "booking_confirmation",
      feed_sub_placement: "below_fold",
      tile_type: "cross_sell_hotel",
      origin_product_type: "air",
      click_target: "view_all",
      channel: isTravelApp ? "App" : isMobile ? "Mobile" : "Web",
    };
    trackEvent({
      eventName: PRODUCT_FEED_TILE_TAPPED,
      properties: {
        ...viewedHotelListProperties.properties,
        ...xSellProps,
      },
      encryptedProperties: [...viewedHotelListProperties.encryptedProperties],
    });
    history.push(
      `${HOTEL_PATH_AVAILABILITY}${transformToStringifiedAvailabilityQuery(
        (searchLocationResult?.id as IIdLodgings).lodgingSelection.searchTerm,
        fromDate,
        untilDate,
        adultsCount,
        children,
        HotelEntryTypeEnum.FLIGHT_CONFIRMATION_VIEW_ALL,
      )}`,
    );
  };
  const onHotelCardClick = (lodgingData: Lodging, index: number) => {
    const { isPreferred, lodging } = lodgingData;
    const available =
      typeof lodgingData.available === "undefined"
        ? true
        : lodgingData.available;
    const xSellProps: ProductFeedTileTappedProperties = {
      feed_type: "cross_sell",
      tile_type: "cross_sell_hotel",
      feed_placement: "booking_confirmation",
      feed_sub_placement: "below_fold",
      click_target: "carousel_card",
      tile_index: index,
      origin_product_type: "air",
      channel: isTravelApp ? "App" : isMobile ? "Mobile" : "Web",
    };
    if (available) {
      trackEvent({
        eventName: PRODUCT_FEED_TILE_TAPPED,
        properties: {
          ...viewedHotelListProperties.properties,
          ...lodgingData?.trackingPropertiesV2?.properties,
          is_preferred_cot: isPreferred,
          lodging_row_index: index,
          is_pc:
            lodgingData.lodgingCollection === LodgingCollectionEnum.Premier,
          is_lc:
            lodgingData.lodgingCollection === LodgingCollectionEnum.Lifestyle,
          ...xSellProps,
        },
        encryptedProperties: [
          ...viewedHotelListProperties.encryptedProperties,
          lodgingData?.trackingPropertiesV2?.encryptedProperties ?? "",
        ],
      });
      setSelectedLodgingIndex(index);
      const params = transformToStringifiedQuery({
        lodgingId: lodging.id,
        lodgingSelection: (searchLocationResult?.id as IIdLodgings)
          .lodgingSelection,
        ...hotelShopParams,
        selectedLodgingIndex: index,
        entryPoint: HotelEntryTypeEnum.FLIGHT_CONFIRMATION_CARD,
        shopToken: isCacheHotelTokenEnabled
          ? lodgingData.price?.opaqueShopRequest
          : localCache.set(uuidv4(), lodgingData.price?.opaqueShopRequest || "")
            .key,
      });

      if (isMobile)
        history.push(`${HOTEL_PATH_SHOP}${params}`, {
          fromPage: location.pathname,
        });
      else window.open(`${HOTEL_PATH_SHOP}${params}`, "_blank");
    }
  };
  {
    return lodgingsToDisplay?.length ? (
      <InView
        onChange={(inView) => {
          if (inView) {
            trackEvent({
              eventName: PRODUCT_FEED_VIEWED_EVENT,
              properties: {
                feed_type: "cross_sell",
                feed_placement: "booking_confirmation",
                feed_sub_placement: "below_fold",
                tile_type: "cross_sell_hotel",
                origin_product_type: "air",
                channel: isTravelApp ? "App" : isMobile ? "Mobile" : "Web",
              },
            });
          }
        }}
        threshold={isMobile ? 0.2 : 0.5}
      >
        <HotelCrossSell
          {...props}
          lodgingsToDisplay={lodgingsToDisplay}
          onViewAllHotelsCTAClick={onViewAllHotelsCTAClick}
          onHotelCardClick={onHotelCardClick}
          showEarnEnhancement={showEarnEnhancement}
          isLodgingPromotionsExperiment={isLodgingPromotionsExperiment}
          className={className}
          isPremierCollectionEnabled={isPremierCollectionEnabled}
          bannerText={offerBannerText}
          onRightButtonClick={() => {
            trackEvent({
              eventName: PRODUCT_FEED_TILE_TAPPED,
              properties: {
                feed_type: "cross_sell",
                feed_placement: "booking_confirmation",
                feed_sub_placement: "below_fold",
                tile_type: "cross_sell_hotel",
                origin_product_type: "air",
                click_target: "carousel_next",
                channel: isTravelApp ? "App" : isMobile ? "Mobile" : "Web",
              },
            });
          }}
          onLeftButtonClick={() => {
            trackEvent({
              eventName: PRODUCT_FEED_TILE_TAPPED,
              properties: {
                feed_type: "cross_sell",
                feed_placement: "booking_confirmation",
                feed_sub_placement: "below_fold",
                tile_type: "cross_sell_hotel",
                origin_product_type: "air",
                click_target: "carousel_prev",
                channel: isTravelApp ? "App" : isMobile ? "Mobile" : "Web",
              },
            });
          }}
        />
      </InView>
    ) : null;
  }
};
